import { Link } from 'react-router-dom'

import { PencilIcon } from '../../components/Icons'
import { useTexte } from '../../texte'

/**
 * Chat, Karte — und für den Reisenden die Werkbank.
 *
 * Chat und Karte sind zwei gleichrangige Ansichten derselben Reise: beides ist
 * der Bericht, nur anders erzählt. Die Reiseseite (`/info`) steht daneben und
 * bleibt, wo sie ist — sie ist Verwaltung und keine Ansicht.
 *
 * **„Bearbeiten" ist der dritte Reiter und nur für den Reisenden da.** Die
 * Kartenseite trug lange zwei Rollen auf einmal; bei 66 Highlights stand einem
 * Mitlesenden das Werkzeug im Weg, das er ohnehin nicht anfassen darf. Ob es
 * erscheint, sagt der Server (`may_edit`) — geraten wird das nicht.
 *
 * **Seit 0.46.0 eine schwebende Pille auf dem Inhalt, nicht mehr eine eigene
 * Zeile unter der Kopfzeile.** Hier stand: „Unter der Kopfzeile statt darin:
 * auf einem Telefon ist der Kopf schon voll." Das stimmt weiterhin — nur
 * kostete die eigene Zeile 64 Punkte, und mit Kopfzeile begann der Verlauf
 * erst bei 129. Aus dem Betrieb kam der Wunsch, die Umschaltung
 * platzsparender zu machen; am Mock-up wurde die Pille gewählt: oben rechts
 * auf dem Inhalt, milchiges Material wie die Blätter, 36 Punkte sichtbar und
 * 44 Trefferfläche. Im Chat weicht sie beim Scrollen und kommt beim Anhalten
 * zurück (`versteckt`), damit sie beim Lesen nach oben nichts verdeckt; auf
 * der Karte bleibt sie stehen. Die Werkbank ist ein Stift ohne Wort — drei
 * Wörter in einer Pille wären auf 390 Punkten breiter als die halbe Karte.
 *
 * **Sie liegt in einem positionierten Rahmen der Seite** (`.chatflaeche`,
 * `.reiseinhalt`), nicht am Fenster: seit dem milchigen Material (#8) ist
 * jedes `backdrop-filter`-Element ein Bezugsrahmen für `position: fixed`,
 * und absolut im Rahmen ist ohnehin das, was gemeint ist.
 */
export function Reiseumschalter({
  tripId,
  aktiv,
  darfBearbeiten = false,
  versteckt = false,
}: {
  tripId: number
  aktiv: 'chat' | 'karte' | 'bearbeiten'
  darfBearbeiten?: boolean
  /** Weggeklappt, solange der Verlauf darunter scrollt. */
  versteckt?: boolean
}) {
  const t = useTexte().karte.umschalter
  const feld = (wohin: typeof aktiv) =>
    aktiv === wohin ? 'umschalter__feld umschalter__feld--aktiv' : 'umschalter__feld'

  return (
    <nav
      className={'umschalter umschalter--schwebend' + (versteckt ? ' umschalter--versteckt' : '')}
      aria-label={t.ansicht}
      data-umschalter={versteckt ? 'versteckt' : 'sichtbar'}
    >
      <Link
        className={feld('chat')}
        to={`/t/${tripId}`}
        aria-current={aktiv === 'chat' ? 'page' : undefined}
        data-umschalter-ziel="chat"
      >
        {t.chat}
      </Link>
      <Link
        className={feld('karte')}
        to={`/t/${tripId}/karte`}
        aria-current={aktiv === 'karte' ? 'page' : undefined}
        data-umschalter-ziel="karte"
      >
        {t.karte}
      </Link>
      {darfBearbeiten && (
        <Link
          className={feld('bearbeiten') + ' umschalter__feld--symbol'}
          to={`/t/${tripId}/karte/bearbeiten`}
          aria-current={aktiv === 'bearbeiten' ? 'page' : undefined}
          aria-label={t.bearbeiten}
          title={t.bearbeiten}
          data-umschalter-ziel="bearbeiten"
        >
          <PencilIcon size={18} />
        </Link>
      )}
    </nav>
  )
}
